import { defaultApiPath, serverApiPath, serverHost } from './constants';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { _notify, NotificationTypes } from './utils/notify';
import { v4 as uuidV4 } from 'uuid';
import { stringify } from 'qs';

export const getServerPath = (host?: string, apiPath?: string): string => {
    host = host || serverHost;
    apiPath = apiPath || serverApiPath || defaultApiPath;

    if (!host) throw new Error('Host variable cannot be undefined');

    return `${host}${apiPath}`;
};

export const getStaticPath = (type?: string) =>
    getServerPath() + '/static' + (type ? `/${type}` : '');

export const baseServerPath: string = process.env.REACT_APP_BACKEND_HOST || '';

export const isValidCode = (code: number) => code.toString().startsWith('2');

const http: AxiosInstance = axios.create({
    withCredentials: true,
    baseURL: getServerPath(),
    paramsSerializer: {
        serialize: (params: Record<string, any>): string => {
            return stringify(params, { arrayFormat: 'repeat' });
        },
    },
});

http.interceptors.request.use(config => {
    if (config.headers) {
        config.headers.Authorization = localStorage.getItem('access-token');
        config.headers['X-Correlation-Id'] = uuidV4();
    }

    return config;
});

http.interceptors.response.use(
    async (config: AxiosResponse): Promise<AxiosResponse> => {
        if (isValidCode(config.status)) {
            if ('data' in config.data) config.data = config.data.data;

            return config;
        }

        _notify(
            window.Toast,
            {
                type: NotificationTypes.Exception,
                content: config.data?.message,
            },
            false
        );
        return Promise.reject(config);
    },
    async error => {
        const originalRequest = error.config;

        if (window.location.pathname !== '/login')
            if (error.response?.status === 401) {
                if (
                    error.response.data?.error === 'Unauthorized' &&
                    originalRequest &&
                    originalRequest._isRetry !== true
                ) {
                    originalRequest._isRetry = true;

                    const { data } = await axios.post(
                        baseServerPath + '/api/auth/refresh',
                        {
                            id: Number(localStorage.getItem('userId')),
                            refreshToken: localStorage.getItem('refresh-token'),
                        },
                        {
                            withCredentials: true,
                        }
                    );

                    localStorage.setItem(
                        'access-token',
                        data?.data?.accessToken
                    );
                    localStorage.setItem(
                        'refresh-token',
                        data?.data?.refreshToken
                    );

                    return http.request(originalRequest);
                } else {
                    localStorage.removeItem('userId');
                    localStorage.removeItem('access-token');
                    localStorage.removeItem('refresh-token');
                    window.location.replace('/login');
                }
            }

        _notify(
            window.Toast,
            {
                type: NotificationTypes.Exception,
                content: error?.errorMessage,
            },
            true
        );

        return Promise.reject(error);
    }
);

export default http;
